import { cn } from '@/lib/utils';

export interface Segment<T extends string> {
  value: T;
  label: string;
  /** Litet antal efter etiketten, som "Alla 9". */
  count?: number;
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Tillgänglig etikett för hela gruppen, t.ex. "Filtrera luckor". */
  label: string;
  className?: string;
}

/**
 * Pillergrupp med vit markör mot tonad bana — kontrollen uppe till höger i
 * tabellkortet i referensbilden.
 *
 * Byggd av riktiga knappar med `role="tab"`-semantik via aria-pressed i stället
 * för en select: valen är få, ömsesidigt uteslutande och ska synas alla på en
 * gång. En dropdown hade gömt filtret bakom ett klick.
 */
export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  label,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      // max-w-full + flex-wrap: gruppen kunde varken brytas eller scrollas, och
      // vyväljaren i Granskning blir bredare än en mobilskärm.
      className={cn(
        'inline-flex max-w-full flex-wrap items-center gap-0.5 p-0.5 rounded-full bg-ink-100',
        className,
      )}
      role="group"
      aria-label={label}
    >
      {segments.map((s) => {
        const aktiv = s.value === value;
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            aria-pressed={aktiv}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors duration-150',
              aktiv
                ? 'bg-surface text-ink-900 shadow-card'
                // ink-600, inte ink-500: den inaktiva texten ligger på den tonade
                // banan (ink-100), inte mot vitt. Uppmätt gav ink-500 där 4,36:1
                // — precis under AA, vilket bara syns om man mäter mot den
                // FAKTISKA bakgrunden och inte mot ytan.
                : 'text-ink-600 hover:text-ink-900',
            )}
          >
            {s.label}
            {s.count != null && (
              <span className="tabular-nums text-[11px] font-bold text-ink-500">{s.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
